import PropTypes from 'prop-types'
import { Map } from 'immutable'

const { videojs } = window
const Button = videojs.getComponent('Button')
export const PLUGIN_NAME = 'VideoOverlayButtons'
const COMPONENT_NAME = 'VideoOverlayButton'
let videoPlayerInfoDisabled = false

// Cross-compatibility for Video.js 5 and 6.
const registerPlugin = videojs.registerPlugin || videojs.plugin

/**
 * A video.js plugin.
 *
 * In the plugin function, the value of `this` is a video.js `Player`
 * instance. You cannot rely on the player being in a "ready" state here,
 * depending on how the plugin is invoked. This may or may not be important
 * to you if not, remove the wait for "ready"!
 *
 * @function videoPlayerOverlayButtons
 * @param    {Object} [options={}]
 * An object of options left to the plugin author to define.
 */
function videoPlayerOverlayButtons (options = {}) {
  this.ready(function onPlayerReady () {
    const player = this
    player.addClass('vjs-video-overlay-buttons')
    // eslint-disable-next-line no-param-reassign
    player.controlBar.videoOverlayButton = player.controlBar.addChild('videoOverlayButton', {
      videoPlayerInfoDisabled: options.videoPlayerInfoDisabled,
      setFeatureTrackingDataPersistent: options.setFeatureTrackingDataPersistent,
      toggleVideoInfoButtonHide: options.toggleVideoInfoButtonHide,
      toggleVideoInfoButtonShow: options.toggleVideoInfoButtonShow,
    }, 13) // Add at index 13 to place it after the captions button
  })
}

/**
 * Button to toggle video overlay
 *
 * @param {Player|Object} player
 * @param {Object=} options
 * @extends Button
 * @class VideoPlayerOverlayToggle
 */
class VideoPlayerOverlayButton extends Button {
  constructor (player, options) {
    super(player, options)
    videoPlayerInfoDisabled = this.options_.videoPlayerInfoDisabled
    if (videoPlayerInfoDisabled) {
      this.controlText(this.localize(this.options_.toggleVideoInfoButtonShow))
      this.removeClass('icon-v2--hide')
      this.addClass('icon-v2--eye')
    } else {
      this.controlText(this.localize(this.options_.toggleVideoInfoButtonHide))
      this.removeClass('icon-v2--eye')
      this.addClass('icon-v2--hide')
    }
  }

  // eslint-disable-next-line class-methods-use-this
  buildCSSClass () {
    const vjsClasses = 'vjs-control vjs-button'
    if (!videoPlayerInfoDisabled) {
      return `${vjsClasses} vjs-video-overlay-button icon-v2 icon-v2--eye`
    }
    return `${vjsClasses} vjs-video-overlay-button icon-v2 icon-v2--hide`
  }

  handleClick () {
    const { player_, options_ } = this
    const { setFeatureTrackingDataPersistent } = options_
    setFeatureTrackingDataPersistent({
      data: Map({ disableVideoInfo: !videoPlayerInfoDisabled }),
    })
    if (videoPlayerInfoDisabled) {
      player_.controlBar.videoOverlayButton.controlText(
        player_.controlBar.videoOverlayButton.localize(options_.toggleVideoInfoButtonHide),
      )
      player_.controlBar.videoOverlayButton.removeClass('icon-v2--eye')
      player_.controlBar.videoOverlayButton.addClass('icon-v2--hide')
    } else {
      player_.controlBar.videoOverlayButton.controlText(
        player_.controlBar.videoOverlayButton.localize(options_.toggleVideoInfoButtonShow),
      )
      player_.controlBar.videoOverlayButton.removeClass('icon-v2--hide')
      player_.controlBar.videoOverlayButton.addClass('icon-v2--eye')
    }

    player_.controlBar.videoOverlayButton.setState({ videoPlayerInfoDisabled })
    videoPlayerInfoDisabled = !videoPlayerInfoDisabled
  }
}

export function register (videojsIn = window.videojs) {
  videojsIn.registerComponent(COMPONENT_NAME, VideoPlayerOverlayButton)
  // Register the plugin with video.js.
  registerPlugin(PLUGIN_NAME, videoPlayerOverlayButtons)
}

VideoPlayerOverlayButton.propTypes = {
  videoPlayerInfoDisabled: PropTypes.bool.isRequired,
  setFeatureTrackingDataPersistent: PropTypes.func.isRequired,
}
